import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AuditDetailTable from './AuditDetailTable';
import { useAuditDetails, useAuditsByDate, useDailyExpandedReport, useAcknowledgeAndVerifiedDetails, useAuditsCompleteThroughDate, useAuditVideo } from '../../hooks';
import AuditVideoPopup from './AuditVideoPopup';
import ReconciliationDialog from './ReconciliationDialog';
import ShiftActivityLedger from './ShiftActivityLedger';
import { CustomButton } from 'components/Button';
import moment from 'moment';
import AuditSummary from './AuditSummary';
import { useStoresSummaryStoresOnly } from 'features/Stores';
import { CircularProgress } from '@mui/material';
import AuditReportHeader from './AuditReportHeader';
import type { PosReceiptItem } from 'models/audit/AuditsByDateModel';
import { getAuditReceiptImage } from 'services/auditService';
import ZoomableImage from 'components/ImagesCroper/ZoomableImage';

type AuditTabValue = 'all' | 'mismatched' | 'moved_to_backstock';

const AUDIT_TAB_QUERY_KEY = 'tab';
const AUDIT_FIX_OCR_PATH = '/dashboard/audit/fix-ocr/';
const APP_BASE_URL = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');

const getAuditFixOcrBaseUrl = (): string | null => {
    if (!APP_BASE_URL || APP_BASE_URL.includes('localhost') || APP_BASE_URL.includes('127.0.0.1')) {
        return 'http://localhost:3001';
    }

    if (APP_BASE_URL === 'https://lottoreco.dev.sofmen.com/api') {
        return 'https://lottoreco-admin.dev.sofmen.com';
    }

    if (APP_BASE_URL === 'https://lottoreco.stg.sofmen.com/api') {
        return 'https://lottoreco-admin.stg.sofmen.com';
    }

    if (APP_BASE_URL === 'https://lottoreco.com/api') {
        return null;
    }

    return 'http://localhost:3001';
};

const parseAuditTab = (value: string | null): AuditTabValue => {
    if (value === 'mismatched' || value === 'moved_to_backstock') {
        return value;
    }
    return 'all';
};

const buildAuditSearch = (auditDate: string | null, activeTab: AuditTabValue): string => {
    const params = new URLSearchParams();
    if (auditDate) {
        params.set('auditDate', auditDate);
    }
    if (activeTab !== 'all') {
        params.set(AUDIT_TAB_QUERY_KEY, activeTab);
    }
    const query = params.toString();
    return query ? `?${query}` : '';
};

const buildPosReceiptReviewUrl = (storeId: string | undefined, receiptQueueId: string) => {
    if (!storeId) return '#';
    const baseUrl = getAuditFixOcrBaseUrl();
    if (!baseUrl) return undefined;

    const params = new URLSearchParams({
        aud_status: 'finalized',
        aud_page: '1',
        aud_store_id: storeId,
        fx_store_id: storeId,
    });
    return `${baseUrl}${AUDIT_FIX_OCR_PATH}${receiptQueueId}?${params.toString()}`;
};

const buildLocalDateFromAuditTimestamp = (auditTimestamp: string): Date | null => {
    const parsed = moment.parseZone(auditTimestamp);
    if (!parsed.isValid()) {
        return null;
    }

    // Preserve the audit's own calendar day instead of reinterpreting the
    // timestamp in the browser timezone.
    return new Date(parsed.year(), parsed.month(), parsed.date(), 12, 0, 0, 0);
};

type ReceiptImageCardProps = {
    queueId: string;
    title?: string;
    caption?: React.ReactNode;
    reviewUrl?: string;
    loadingText: string;
};

const ReceiptImageCard: React.FC<ReceiptImageCardProps> = ({
    queueId,
    title,
    caption,
    reviewUrl,
    loadingText,
}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoadingImage, setIsLoadingImage] = useState(true);
    const [isClosed, setIsClosed] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let objectUrl: string | null = null;

        const loadImage = async () => {
            try {
                const blob = await getAuditReceiptImage(queueId);
                if (!isMounted || !blob) return;
                objectUrl = URL.createObjectURL(blob);
                setImageUrl(objectUrl);
            } finally {
                if (isMounted) {
                    setIsLoadingImage(false);
                }
            }
        };

        setImageUrl(null);
        setIsLoadingImage(true);
        setIsClosed(false);
        loadImage();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [queueId]);

    if (isClosed) return null;
    if (!isLoadingImage && !imageUrl) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '30vw',
                minWidth: '30vw',
                height: '1200px',
                pt: imageUrl && title ? 2 : 0,
            }}
        >
            {imageUrl && title && (
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#222B45' }}>
                    {title}
                </Typography>
            )}
            {imageUrl && caption && reviewUrl ? (
                <Typography
                    component="a"
                    href={reviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: '#1976D2',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {caption}
                </Typography>
            ) : imageUrl && caption ? (
                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: '#4E5A6E' }}>
                    {caption}
                </Typography>
            ) : null}
            <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
                {imageUrl ? (
                    <ZoomableImage
                        imageUrl={imageUrl}
                        onClose={() => setIsClosed(true)}
                    />
                ) : isLoadingImage ? (
                    <Box sx={{ p: 2, color: '#7A8497', fontSize: 13 }}>{loadingText}</Box>
                ) : null}
            </Box>
        </Box>
    );
};

const formatPosReceiptSummary = (receipt: PosReceiptItem) => {
    const reportDate = receipt.report_date ? moment(receipt.report_date).format('MM/DD') : 'date n/a';
    const reportTime = receipt.report_time ? moment(receipt.report_time, 'HH:mm:ss').format('h:mm a') : 'time n/a';
    const location = receipt.location_key || 'Vending';
    const scratcherAmount = Number(receipt.scratcher_amount || 0).toFixed(2);
    const drawGameAmount = Number(receipt.draw_game_amount || 0).toFixed(2);
    const relationLabel = receipt.window_relation === 'before_period'
        ? 'Before period | '
        : receipt.window_relation === 'after_period'
            ? 'After period | '
            : '';
    return `${relationLabel}${location} ${reportDate} @ ${reportTime} | Scratchers ${receipt.scratcher_count || 0} / $${scratcherAmount} | Draw $${drawGameAmount}`;
};

const PosReceiptImageCard: React.FC<{
    receipt: PosReceiptItem;
    storeId: string | undefined;
}> = ({ receipt, storeId }) => {
    return (
        <ReceiptImageCard
            queueId={receipt.receipt_audit_queue_id}
            title=""
            caption={formatPosReceiptSummary(receipt)}
            reviewUrl={buildPosReceiptReviewUrl(storeId, receipt.receipt_audit_queue_id)}
            loadingText="Loading POS receipt..."
        />
    );
};

const PosReceiptPanel: React.FC<{
    receipts: PosReceiptItem[];
    storeId: string | undefined;
    title: string;
}> = ({ receipts, storeId, title }) => {
    if (!receipts.length) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '1200px',
                width: receipts.length > 1
                    ? `calc(${receipts.length} * 30vw + ${(receipts.length - 1) * 16}px)`
                    : '30vw',
                minWidth: receipts.length > 1
                    ? `calc(${receipts.length} * 30vw + ${(receipts.length - 1) * 16}px)`
                    : '30vw',
                pt: 2,
                overflow: 'visible',
            }}
        >
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#222B45', mb: 1 }}>
                {title}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'visible',
                    pb: 1,
                }}
            >
                {receipts.map((receipt) => (
                    <PosReceiptImageCard
                        key={receipt.receipt_audit_queue_id}
                        receipt={receipt}
                        storeId={storeId}
                    />
                ))}
            </Box>
        </Box>
    );
};

const AuditDetail: React.FC = () => {
    const { id, auditId } = useParams<{ id: string; auditId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const searchString = searchParams.toString();

    const selectedShift = auditId || 'All Shift';

    const { data: auditDetails, isLoading: isLoadingAuditDetails } = useAuditDetails(id, auditId);

    // Fetch stores to get pos_type
    const { data: allStores, isLoading: isLoadingStores } = useStoresSummaryStoresOnly();

    // Get current store details
    const storeDetails = useMemo(() => {
        if (!allStores || !id) return undefined;
        return allStores.find((item) => item.store.id === id)?.store;
    }, [allStores, id]);

    const isBothConnected = !!(storeDetails?.pos_type && storeDetails?.pos_type !== "n/a" && storeDetails?.lotto_authorized);
    const isBothNotConnected = !!((!storeDetails?.pos_type || storeDetails?.pos_type === "n/a") && (!storeDetails?.lotto_authorized));
    const isLottoConnectedOnly = !!(storeDetails?.lotto_authorized && (!storeDetails?.pos_type || storeDetails?.pos_type === "n/a"));
    const isPOSConnectedOnly = !!(storeDetails?.pos_type && storeDetails?.pos_type !== "n/a" && !storeDetails?.lotto_authorized);

    const hasPosType = isBothConnected || isPOSConnectedOnly;



    // UI State
    const [openReceipts, setOpenReceipts] = useState(!!auditId);
    const [openVideo, setOpenVideo] = useState(false);
    const [auditVideoUrl, setAuditVideoUrl] = useState<string | null>(null);
    const [openReconciliation, setOpenReconciliation] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [activeTab, setActiveTab] = useState<AuditTabValue>(() => parseAuditTab(searchParams.get(AUDIT_TAB_QUERY_KEY)));
    const [previousStoreId, setPreviousStoreId] = useState<string | undefined>(id);
    const fetchAuditVideoMutation = useAuditVideo();

    useEffect(() => {
        const nextTab = parseAuditTab(new URLSearchParams(searchString).get(AUDIT_TAB_QUERY_KEY));
        setActiveTab((currentTab) => currentTab === nextTab ? currentTab : nextTab);
    }, [searchString]);

    // Reset to "all" if mismatched tab becomes unavailable.
    useEffect(() => {
        if (!hasPosType && activeTab === 'mismatched') {
            setActiveTab('all');
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete(AUDIT_TAB_QUERY_KEY);
            setSearchParams(nextParams, { replace: true });
        }
    }, [hasPosType, activeTab, searchParams, setSearchParams]);

    // Open receipt by default when auditId is present, close when auditId is removed
    useEffect(() => {
        if (auditId) {
            setOpenReceipts(true);
        } else {
            // Close receipts when switching to "All Shift" (auditId removed from URL)
            setOpenReceipts(false);
        }
        setOpenVideo(false);
        setAuditVideoUrl(null);
    }, [auditId]);

    // Keep the URL date as the source of truth when it exists so the picker
    // and downstream API calls stay aligned.
    const dateParam = searchParams.get('auditDate');

    useEffect(() => {
        if (dateParam) {
            const parsedDate = moment(dateParam, 'YYYY-MM-DD', true);
            if (parsedDate.isValid()) {
                const nextDate = parsedDate.toDate();
                // Update if selectedDate is null OR if it's different from the URL date
                if (!selectedDate || !moment(selectedDate).isSame(nextDate, 'day')) {
                    setSelectedDate(nextDate);
                }
            }
            return;
        }

        if (auditId && auditDetails?.auditDate) {
            const auditDateObj = buildLocalDateFromAuditTimestamp(auditDetails.auditDate);
            if (auditDateObj && (!selectedDate || !moment(selectedDate).isSame(auditDateObj, 'day'))) {
                setSelectedDate(auditDateObj);
            }
        }
    }, [auditDetails?.auditDate, auditId, dateParam, selectedDate]);

    // Format date for API call
    const auditDate = useMemo(() => {
        if (selectedDate) {
            return moment(selectedDate).format('YYYY-MM-DD');
        }
        return null;
    }, [selectedDate]);

    // When store changes, remove auditId from URL but keep the date
    useEffect(() => {
        // Only trigger if store actually changed (not on initial mount)
        if (previousStoreId && id && id !== previousStoreId && auditId) {
            // Store changed and auditId exists - remove auditId but keep date
            navigate(`/store/${id}/audits${buildAuditSearch(auditDate, activeTab)}`, { replace: true });
        }
        // Update previousStoreId after checking (but only if id exists to avoid undefined)
        // Optimization: Only set if different to avoid redundant updates
        if (id && id !== previousStoreId) {
            setPreviousStoreId(id);
        }
    }, [id, auditId, auditDate, activeTab, navigate, previousStoreId]);

    // Fetch audits by date
    const { data: auditsByDateModel, isLoading: isLoadingAuditsByDate, error: auditsByDateError } = useAuditsByDate(id, auditDate);
    const posReceipts = useMemo(() => (
        (auditsByDateModel?.getPosReceipts() || []).filter((receipt) => (
            selectedShift === 'All Shift' || receipt.audit_id === selectedShift
        ))
    ), [auditsByDateModel, selectedShift]);
    const salesReceiptsInPeriod = useMemo(() => (
        posReceipts.filter((receipt) => (receipt.window_relation || 'in_period') === 'in_period')
    ), [posReceipts]);
    const salesReceiptsAroundPeriod = useMemo(() => {
        const relationOrder = { before_period: 0, after_period: 1 } as const;
        return posReceipts
            .filter((receipt) => receipt.window_relation === 'before_period' || receipt.window_relation === 'after_period')
            .sort((a, b) => (
                relationOrder[a.window_relation as 'before_period' | 'after_period']
                - relationOrder[b.window_relation as 'before_period' | 'after_period']
            ));
    }, [posReceipts]);
    const selectedAuditItem = useMemo(() => (
        (auditsByDateModel?.getAudits() || []).find((audit) => audit.audit_id === selectedShift)
    ), [auditsByDateModel, selectedShift]);
    const inventoryReceipts = useMemo(() => {
        if (!selectedAuditItem) return [];

        const receipts: Array<{
            key: string;
            queueId: string;
            title: string;
            caption: string;
        }> = [];

        if (selectedAuditItem.previous_receipt_audit_queue_id) {
            const previousTime = selectedAuditItem.previous_audit_time
                ? moment(selectedAuditItem.previous_audit_time).format('MM/DD @ h:mm a')
                : 'previous audit';
            receipts.push({
                key: `previous-${selectedAuditItem.previous_receipt_audit_queue_id}`,
                queueId: selectedAuditItem.previous_receipt_audit_queue_id,
                title: 'Previous Inventory Receipt',
                caption: previousTime,
            });
        }

        if (selectedAuditItem.receipt_audit_queue_id) {
            const currentTime = selectedAuditItem.time
                ? moment(selectedAuditItem.time).format('MM/DD @ h:mm a')
                : 'current audit';
            receipts.push({
                key: `current-${selectedAuditItem.receipt_audit_queue_id}`,
                queueId: selectedAuditItem.receipt_audit_queue_id,
                title: 'Current Inventory Receipt',
                caption: currentTime,
            });
        }

        return receipts;
    }, [selectedAuditItem]);

    // Fetch audits complete through date only when both integrations are connected
    const { data: auditsCompleteThroughDate } = useAuditsCompleteThroughDate(
        isBothConnected ? id : null
    );

    // Fetch acknowledge and verified details (new API for notes, verified, ack)
    const { data: acknowledgeAndVerifiedModel, isLoading: isLoadingAcknowledgeAndVerified } = useAcknowledgeAndVerifiedDetails(id, auditDate);

    // Fetch daily expanded report only when "All Shift" is selected
    const shouldFetchDailyReport = selectedShift === 'All Shift';
    const { data: dailyReportResponse, isLoading: isLoadingDailyReport } = useDailyExpandedReport(
        id,
        shouldFetchDailyReport ? auditDate : null
    );

    const isLoadingSummary = isLoadingAuditsByDate ||
        (selectedShift === 'All Shift' ? isLoadingDailyReport : isLoadingAuditDetails);

    // Use model's internal transformation for daily report items
    const dailyReportItems = dailyReportResponse?.items || [];

    const filteredData = useMemo(() => {
        // Use transformed daily report data when "All Shift" is selected, otherwise use audit details
        const details = selectedShift === 'All Shift'
            ? dailyReportItems
            : (auditDetails?.items || []);

        if (activeTab === 'mismatched' && hasPosType) {
            return details.filter((item) => item.hasVariance);
        }

        if (activeTab === 'moved_to_backstock') {
            return details.filter((item: any) => {
                const movedCount = Number(
                    item?.movedToBackstockCount ??
                    item?.moved_to_backstock_count ??
                    item?.backstockMovedCount ??
                    0
                );
                const movedFlag = Boolean(
                    item?.isMovedToBackstock ??
                    item?.is_moved_to_backstock ??
                    item?.movedToBackstock
                );

                return movedFlag || movedCount > 0;
            });
        }

        return details;
    }, [selectedShift, dailyReportItems, auditDetails?.items, activeTab, hasPosType]);

    const movedToBackstockData = useMemo<any[]>(() => (
        selectedShift === 'All Shift'
            ? (dailyReportResponse?.movedToBackstockItems || [])
            : (auditDetails?.movedToBackstockItems || [])
    ), [selectedShift, dailyReportResponse?.movedToBackstockItems, auditDetails?.movedToBackstockItems]);

    const movedToBackstockCount = useMemo(() => (
        selectedShift === 'All Shift'
            ? (dailyReportResponse?.movedToBackstockItems?.length ?? 0)
            : (auditDetails?.movedToBackstockItems?.length ?? 0)
    ), [selectedShift, dailyReportResponse?.movedToBackstockItems, auditDetails?.movedToBackstockItems]);

    const isMovedToBackstockLoading = selectedShift === 'All Shift'
        ? isLoadingDailyReport
        : isLoadingAuditDetails;

    const gamesCount = useMemo(() => {
        const games = selectedShift === 'All Shift'
            ? (dailyReportResponse as any)?._serverData?.games
            : (auditDetails as any)?._serverData?.games;

        return Array.isArray(games) ? games.length : 0;
    }, [selectedShift, dailyReportResponse, auditDetails]);

    const mismatchedCount = useMemo(() => {
        const games = selectedShift === 'All Shift'
            ? (dailyReportResponse as any)?._serverData?.games
            : (auditDetails as any)?._serverData?.games;

        if (!Array.isArray(games)) return 0;

        return games.reduce((count: number, game: any) => {
            const quantityVariance = Number(game?.reconciliation_variance?.quantity_variance ?? 0);
            const revenueVariance = Number(game?.reconciliation_variance?.revenue_variance ?? 0);
            return (quantityVariance !== 0 || revenueVariance !== 0) ? count + 1 : count;
        }, 0);
    }, [selectedShift, dailyReportResponse, auditDetails]);

    const totalPacksCount = useMemo(() => {
        if (activeTab === 'moved_to_backstock') {
            return movedToBackstockCount;
        }

        const games = selectedShift === 'All Shift'
            ? (dailyReportResponse as any)?._serverData?.games
            : (auditDetails as any)?._serverData?.games;

        if (!Array.isArray(games)) return 0;

        const gamesToCount = (activeTab === 'mismatched' && hasPosType)
            ? games.filter((game: any) => {
                const quantityVariance = Number(game?.reconciliation_variance?.quantity_variance ?? 0);
                const revenueVariance = Number(game?.reconciliation_variance?.revenue_variance ?? 0);
                return quantityVariance !== 0 || revenueVariance !== 0;
            })
            : games;

        return gamesToCount.reduce((sum: number, game: any) => {
            const packs = Array.isArray(game?.packs) ? game.packs.length : 0;
            return sum + packs;
        }, 0);
    }, [
        activeTab,
        movedToBackstockCount,
        selectedShift,
        dailyReportResponse,
        auditDetails,
        hasPosType
    ]);

    const isMismatchedLoading = selectedShift === 'All Shift'
        ? isLoadingDailyReport
        : isLoadingAuditDetails;

    const tableData = useMemo(() => (
        activeTab === 'moved_to_backstock'
            ? movedToBackstockData
            : (filteredData || [])
    ), [activeTab, movedToBackstockData, filteredData]);

    const shiftActivity = useMemo(() => (
        auditId ? (auditDetails?.shiftActivity || []) : []
    ), [auditId, auditDetails?.shiftActivity]);

    const auditNoteEntries = auditId && selectedShift !== 'All Shift'
        ? (auditDetails?.noteEntries || [])
        : [];

    // Determine loading state for the table
    const isLoadingTable = selectedShift === 'All Shift'
        ? isLoadingDailyReport
        : isLoadingAuditDetails;

    const suggestedAmount = useMemo(() => {
        if (selectedShift !== 'All Shift') return 0;

        return dailyReportResponse?.summaryTotals?.reconciliation_variance?.total_variance || 0;
    }, [selectedShift, dailyReportResponse]);

    const isPosValueMissing = useMemo(() => {
        const effectiveDate = auditsCompleteThroughDate?.data?.effective_date;
        const posTotals = dailyReportResponse?.summaryTotals?.pos;

        const isEffectiveDateAfterAuditDate = Boolean(
            effectiveDate &&
            auditDate &&
            moment(effectiveDate).isAfter(moment(auditDate))
        );

        const isExpandedPosDataAllZero = Boolean(
            posTotals &&
            Number(posTotals.lotto_sales ?? 0) === 0 &&
            Number(posTotals.payout ?? 0) === 0 &&
            Number(posTotals.scratcher_count ?? 0) === 0 &&
            Number(posTotals.scratcher_sales ?? 0) === 0
        );

        return isEffectiveDateAfterAuditDate && isExpandedPosDataAllZero;
    }, [auditsCompleteThroughDate?.data?.effective_date, auditDate, dailyReportResponse?.summaryTotals?.pos]);

    const handleOpenVideo = useCallback(async () => {
        if (!auditId) return;

        const videoUrl = await fetchAuditVideoMutation.mutateAsync(auditId);
        if (videoUrl) {
            setAuditVideoUrl(videoUrl);
            setOpenVideo(true);
        }
    }, [auditId, fetchAuditVideoMutation]);

    const handleCloseVideo = useCallback(() => {
        setOpenVideo(false);
        setAuditVideoUrl(null);
    }, []);

    // Optimized Handlers with useCallback
    const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: AuditTabValue) => {
        setActiveTab(newValue);
        const nextParams = new URLSearchParams(searchParams);
        if (newValue === 'all') {
            nextParams.delete(AUDIT_TAB_QUERY_KEY);
        } else {
            nextParams.set(AUDIT_TAB_QUERY_KEY, newValue);
        }
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleShiftChange = useCallback((newAuditId: string) => {
        // Always include auditDate parameter for consistency
        const queryString = buildAuditSearch(auditDate, activeTab);
        if (newAuditId === 'All Shift') {
            // Navigate to audits page without auditId, preserving auditDate
            navigate(`/store/${id}/audits${queryString}`);
        } else {
            // Navigate to specific audit with auditDate parameter
            navigate(`/store/${id}/audits/${newAuditId}${queryString}`);
        }
    }, [auditDate, activeTab, id, navigate]);

    const handleDateChange = useCallback((date: Date | null) => {
        setSelectedDate(date);

        // Update URL - always navigate to audits page without auditId when date changes
        // because date change defaults to "All Shift" which doesn't have an auditId
        if (date) {
            const dateParam = moment(date).format('YYYY-MM-DD');
            navigate(`/store/${id}/audits${buildAuditSearch(dateParam, activeTab)}`);
        } else {
            // If date is cleared, navigate to audits page without date param while preserving the tab.
            navigate(`/store/${id}/audits${buildAuditSearch(null, activeTab)}`);
        }
    }, [activeTab, id, navigate]);

    // Check if we have a "No audit found" error (useAuditsByDate failed)
    const isNoAuditFoundError = useMemo(() => {
        if (!auditsByDateError) return false;
        const errorMessage = (auditsByDateError as any)?.response?.data?.detail ||
            (auditsByDateError as any)?.response?.data?.message ||
            auditsByDateError?.message || '';
        return errorMessage.includes('No audit found');
    }, [auditsByDateError]);

    const showNoAuditFound = isNoAuditFoundError && !isLoadingAuditsByDate;

    // When auditsByDate fails but daily report has data, still show summary tiles and shift dropdown
    const hasDailyReportData = useMemo(() => !!(
        (dailyReportResponse?.items && dailyReportResponse.items.length > 0) ||
        dailyReportResponse?.summaryTotals != null
    ), [dailyReportResponse]);

    // Only hide cards when we have no audit AND no daily report data to show
    const hideCards = showNoAuditFound && !hasDailyReportData;

    return (
        <div>
            <AuditReportHeader
                dailyReportResponse={dailyReportResponse}
                isAllShift={selectedShift === 'All Shift'}
                storeName={storeDetails?.name}
                storeAddress={storeDetails?.address_line1}
                isBothConnected={isBothConnected}
                isBothNotConnected={isBothNotConnected}
                isLottoConnectedOnly={isLottoConnectedOnly}
                isPOSConnectedOnly={isPOSConnectedOnly}
            />
            <AuditSummary
                auditsByDateModel={auditsByDateModel ?? null}
                acknowledgeAndVerifiedModel={acknowledgeAndVerifiedModel ?? null}
                selectedShift={selectedShift}
                onShiftChange={handleShiftChange}
                date={selectedDate}
                onDateChange={handleDateChange}
                auditTotals={selectedShift === 'All Shift'
                    ? dailyReportResponse?.summaryTotals
                    : auditDetails?.summaryTotals}
                dailyTotals={dailyReportResponse?.summaryTotals}
                selectedAuditId={auditId}
                isLoading={isLoadingSummary}
                isLoadingStores={isLoadingStores}
                isLoadingAcknowledgeAndVerified={isLoadingAcknowledgeAndVerified}
                hideCards={hideCards}
                isBothConnected={isBothConnected}
                isBothNotConnected={isBothNotConnected}
                isLottoConnectedOnly={isLottoConnectedOnly}
                isPOSConnectedOnly={isPOSConnectedOnly}
                isPosValueMissing={isPosValueMissing}
                storeId={id}
                storeUserRole={(storeDetails as any)?.user_role}
                canApproveReconciliation={Boolean((storeDetails as any)?.can_approve_reconciliation)}
            />
            <div style={{ marginTop: 12, padding: 0, marginBottom: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", md: "center" },
                        flexDirection: { xs: "column", md: "row" },
                        gap: { xs: 1, md: 0 },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#222B45",
                            fontFamily: "Inter",
                        }}
                    >
                        Scratcher <span
                            style={{
                                fontSize: "20px",
                                fontWeight: 600,
                            }}
                        >
                            (
                            {(isLoadingDailyReport || isLoadingAuditDetails) ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                gamesCount
                            )}
                            )
                        </span>
                    </Typography>

                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        className="audit-segmented-tabs"
                        sx={{
                            mt: { xs: 0.5, md: 0 },
                            width: { xs: '100%', md: 'auto' },
                            minHeight: 'unset',
                            '& .MuiTabs-flexContainer': {
                                gap: { xs: 0.5, md: '4px' },
                                flexWrap: { xs: 'wrap', md: 'nowrap' },
                                alignItems: 'flex-end',
                            },
                            '& .MuiTab-root': {
                                flex: { xs: '0 0 auto', md: '0 1 auto' },
                                minWidth: { xs: 'auto', md: '90px' },
                                px: { xs: 1, md: 1.25 },
                                py: { xs: 0.75, md: 1 },
                                whiteSpace: 'nowrap',
                            },
                            '@media (max-width: 567px)': {
                                '& .MuiTabs-flexContainer': {
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    gap: 0.5,
                                },
                                '& .MuiTab-root': {
                                    width: '100%',
                                    maxWidth: 'none',
                                    minWidth: 0,
                                    flex: '1 1 100%',
                                    justifyContent: 'flex-start',
                                },
                            },
                        }}
                    >
                        <Tab label="All" value="all" />
                        {hasPosType && (
                            <Tab
                                value="mismatched"
                                label={
                                    <span className="d-flex items-center gap-4">
                                        <span>Mismatched</span>
                                        <span>
                                            (
                                            {isMismatchedLoading ? (
                                                <CircularProgress size={12} color="inherit" />
                                            ) : (
                                                mismatchedCount
                                            )}
                                            )
                                        </span>
                                    </span>
                                }
                            />
                        )}
                        <Tab
                            value="moved_to_backstock"
                            label={
                                <span className="d-flex items-center gap-4">
                                    <span>Moved to Backstock</span>
                                    <span>
                                        (
                                        {isMovedToBackstockLoading ? (
                                            <CircularProgress size={12} color="inherit" />
                                        ) : (
                                            movedToBackstockCount
                                        )}
                                        )
                                    </span>
                                </span>
                            }
                        />
                    </Tabs>
                </Box>

                <AuditDetailTable
                    data={tableData as any}
                    isLoading={isLoadingTable}
                    isBothConnected={isBothConnected}
                    isBothNotConnected={isBothNotConnected}
                    isLottoConnectedOnly={isLottoConnectedOnly}
                    isPOSConnectedOnly={isPOSConnectedOnly}
                    tableView={activeTab === 'moved_to_backstock' ? 'moved_to_backstock' : 'default'}
                    totalPacksCount={totalPacksCount}
                    auditNotes={auditNoteEntries}
                />
                <ShiftActivityLedger
                    activities={shiftActivity}
                    barcodeFormat={(storeDetails as any)?.barcode_format}
                />
            </div>

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 3,
                mb: 0
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {auditId && !openReceipts && (
                        <CustomButton onClick={() => setOpenReceipts(true)}>View Receipt</CustomButton>
                    )}
                    {auditId && auditDetails?.hasVideo && !openVideo && (
                        <CustomButton
                            onClick={handleOpenVideo}
                            loading={fetchAuditVideoMutation.isPending}
                            loadingText="Loading..."
                            startIcon={<PlayCircleOutlineIcon fontSize="small" />}
                        >
                            View Video
                        </CustomButton>
                    )}
                </Box>
                {selectedShift === 'All Shift' &&
                    hasPosType &&
                    dailyReportResponse?.summaryTotals.reconciliation_variance?.total_variance !== 0 && (
                        <Box
                            onClick={() => setOpenReconciliation(true)}
                            sx={{
                                fontWeight: 500,
                                fontSize: '18px',
                                textAlign: 'right',
                                color: '#0000FF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                        >
                            Create Reconciliation
                        </Box>
                    )}
            </Box>
            {auditId && openReceipts && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%', overflowX: 'auto' }}>
                        {inventoryReceipts.map((receipt) => (
                            <ReceiptImageCard
                                key={receipt.key}
                                queueId={receipt.queueId}
                                title={receipt.title}
                                caption={receipt.caption}
                                reviewUrl={buildPosReceiptReviewUrl(id, receipt.queueId)}
                                loadingText="Loading inventory receipt..."
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%', overflowX: 'auto' }}>
                        <PosReceiptPanel
                            receipts={salesReceiptsInPeriod}
                            storeId={id}
                            title="Sales in Period"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%', overflowX: 'auto' }}>
                        <PosReceiptPanel
                            receipts={salesReceiptsAroundPeriod}
                            storeId={id}
                            title="Sales Before / After Period"
                        />
                    </Box>
                </Box>
            )}
            {auditId && openVideo && auditVideoUrl && (
                <AuditVideoPopup
                    open={openVideo}
                    handleClose={handleCloseVideo}
                    videoUrl={auditVideoUrl}
                />
            )}
            {id && auditDate && (
                <ReconciliationDialog
                    open={openReconciliation}
                    onClose={() => setOpenReconciliation(false)}
                    suggestedAmount={suggestedAmount}
                    storeId={id}
                    date={auditDate}
                />
            )}
        </div>
    );
};

export default AuditDetail;
